import { call } from "redux-saga/effects";

import { PLUGIN_ID_TO_PROTO_CLASS } from "@superblocksteam/shared";
import type { IntegrationDto, IntegrationKind } from "@superblocksteam/shared";

import { normalizeDatasourceConfiguration } from "core/utils/normalizeDatasourceConfiguration";
import { ROOT } from "store/utils/types";
import type { EntitiesStateWithMeta } from "store/utils/types";

import { createSaga } from "../../../utils/saga";
import { getDatasources } from "../client";
import slice from "../slice";
import type { DatasourceDtoViews, DatasourceMeta } from "../slice";

interface GetDatasourcesPayload {
  organizationId: string;
  kind?: IntegrationKind;
}

function* getDatasourcesInternal({
  organizationId,
  kind,
}: GetDatasourcesPayload) {
  const fullDtos: IntegrationDto[] = yield call(
    getDatasources,
    organizationId,
    kind,
  );
  const integrations = [];
  for (const integration of fullDtos) {
    if (PLUGIN_ID_TO_PROTO_CLASS[integration.pluginId]) {
      const protoConfigs = integration.configurations.map((config) => ({
        ...config,
        configuration: normalizeDatasourceConfiguration(
          integration.pluginId,
          config.configuration,
        ),
      }));
      integrations.push({ ...integration, configurations: protoConfigs });
    } else {
      integrations.push(integration);
    }
  }
  return integrations;
}

const getDatasourcesSaga = createSaga(
  getDatasourcesInternal,
  "getDatasourcesSaga",
  {
    sliceName: "datasources",
  },
);

slice.saga(getDatasourcesSaga, {
  start(state, { payload }) {
    state.loading[ROOT] = true;
    state.meta[ROOT] = { organizationId: payload.organizationId };
    delete state.errors[ROOT];
  },
  success(state, { payload, meta }) {
    modifyState(state, payload, meta.args.kind);
    delete state.loading[ROOT];
  },
  error(state, { payload }) {
    state.errors[ROOT] = { error: payload };
    delete state.loading[ROOT];
  },
  store(state, { payload }) {
    modifyState(state, payload);
  },
});

const modifyState = (
  state: EntitiesStateWithMeta<DatasourceDtoViews, DatasourceMeta>,
  payload: IntegrationDto[],
  kind?: IntegrationKind,
): void => {
  const notSeen = new Set(
    Object.keys(state.entities).filter((entKey) =>
      kind ? state.entities[entKey].kind === kind : true,
    ),
  );
  state.entities = payload.reduce(
    (result, datasource) => {
      notSeen.delete(datasource.id);
      result[datasource.id] = Object.assign(result[datasource.id] ?? {}, {
        integration: datasource,
        kind: state.entities?.[datasource.id]?.kind ?? datasource.kind,
      });
      return result;
    },
    state.entities as Record<string, DatasourceDtoViews>,
  );
  notSeen.forEach((id) => (state.entities[id].integration = undefined));
};
